// fe-ios12/src/components/NotificationsPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import useAuth from '../hooks/useAuth';
import api from './api';

const FIELD_LABELS = {
  mark_over:       'Mark session over',
  sessionComments: 'Session comments',
  recommendations: 'Recommendations',
  readings:        'ROM / body readings',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function NotificationsPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { auth } = useAuth();
  const { notifications, acknowledgeNotification, markRead, fetchNotifications } = useNotifications() || {};
  const focusId   = location.state?.focusId      // passed from bell/profile click
    || new URLSearchParams(location.search).get('focus');  // from push notification deep-link
  const focusRef  = useRef(null);

  const isSuper = auth?.roles?.some(r => ['owner', 'solution'].includes(r));
  const isPatientRole = auth?.roles?.includes('saadhak');
  const canViewAll = isSuper; // only owner/solution see all tabs

  const [viewMode, setViewMode] = useState('mine');
  const [allNotifs, setAllNotifs] = useState(null);

  // Refresh on mount
  useEffect(() => { fetchNotifications?.(); }, []); // eslint-disable-line

  // Fetch all staff or patient notifications when admin toggles view
  useEffect(() => {
    if ((viewMode === 'all' || viewMode === 'patients') && canViewAll) {
      api.get(`${process.env.REACT_APP_URL}/notifications?view=${viewMode}`)
        .then(res => setAllNotifs(res.data || []))
        .catch(() => setAllNotifs([]));
    } else {
      setAllNotifs(null);
    }
  }, [viewMode]); // eslint-disable-line

  // Scroll to focused notification, mark it read, and expand its details
  useEffect(() => {
    if (focusId) {
      setExpandedDetails(prev => ({ ...prev, [focusId]: true }));
      if (markRead) markRead(focusId);
    }
  }, [focusId]); // eslint-disable-line

  const displayNotifs = (viewMode === 'all' || viewMode === 'patients') && allNotifs !== null ? allNotifs : (notifications || []);
  const active = displayNotifs.filter(n => !n.resolved);

  // Scroll after render when focusRef is set
  useEffect(() => {
    if (focusId && focusRef.current) {
      setTimeout(() => focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    }
  }, [focusId, active.length]); // re-trigger when notifications load

  // ── Group by type ─────────────────────────────────────────────────────────
  const STAFF_GROUPS = [
    { key: 'SYSTEM_CRITICAL',   label: 'Critical',              icon: '🔴', color: '#dc2626', critical: true },
    { key: 'GEO_EXIT_ALERT',    label: 'Geo-fence Alerts',      icon: '📍', color: '#ef4444', critical: true },
    { key: 'ESCALATION',        label: 'Escalations',           icon: '🚨', color: '#ef4444', critical: true },
    { key: 'SESSION_PENDING',   label: 'Session Pending',       icon: '⚠️', color: '#f97316', critical: false },
    { key: 'PAYMENT_PENDING',   label: 'Payment Pending',       icon: '💳', color: '#eab308', critical: false },
    { key: 'PLAN_APPROVAL',     label: 'Plan Approvals',        icon: '📋', color: '#3b82f6', critical: false },
    { key: 'TASK_REMINDER',     label: 'Task Reminders',        icon: '📋', color: '#8b5cf6', critical: false },
    { key: 'TASK_ASSIGNED',     label: 'Tasks Assigned',        icon: '📌', color: '#7c3aed', critical: false },
    { key: 'CHECKLIST_MISSED',  label: 'Checklist Missed',      icon: '📝', color: '#f59e0b', critical: false },
    { key: 'INACTIVE_PATIENT',  label: 'Inactive Saadhaks',     icon: '👤', color: '#64748b', critical: false },
    { key: 'RENEWAL_REMINDER',  label: 'Renewal Reminders',     icon: '🔄', color: '#06b6d4', critical: false },
    { key: 'APPOINTMENT_REMINDER', label: 'Appointment Reminders', icon: '📅', color: '#10b981', critical: false },
    { key: 'BOOKING_ALERT',     label: 'Booking Alerts',        icon: '📩', color: '#f472b6', critical: false },
    { key: 'MORNING_GREETING',  label: 'Morning Greetings',     icon: '☀️', color: '#fbbf24', critical: false },
  ];

  const PATIENT_GROUPS = [
    { key: 'PATIENT_APPT_TOMORROW', label: 'Appointment Tomorrow',  icon: '📅', color: '#3b82f6', critical: false },
    { key: 'PATIENT_APPT_SOON',     label: 'Appointment Soon',      icon: '⏰', color: '#f97316', critical: false },
    { key: 'PATIENT_APPT_SCHEDULED',label: 'Appointment Scheduled', icon: '✅', color: '#10b981', critical: false },
    { key: 'PATIENT_APPT_COMPLETED',label: 'Session Completed',     icon: '🎉', color: '#22c55e', critical: false },
    { key: 'PATIENT_APPT_MISSED',   label: 'Session Missed',        icon: '❌', color: '#ef4444', critical: true },
    { key: 'PATIENT_VIDEO_ASSIGNED',label: 'Videos Assigned',        icon: '🎬', color: '#8b5cf6', critical: false },
    { key: 'PATIENT_PLAYLIST_ASSIGNED',label: 'Playlists Assigned',  icon: '📺', color: '#7c3aed', critical: false },
    { key: 'PATIENT_PAYMENT_RECEIVED',label: 'Payment Received',     icon: '💳', color: '#06b6d4', critical: false },
    { key: 'PATIENT_BALANCE_DUE',   label: 'Balance Due',           icon: '💰', color: '#eab308', critical: false },
    { key: 'PATIENT_REPORT_UPLOADED',label: 'Report Uploaded',       icon: '📄', color: '#64748b', critical: false },
    { key: 'PATIENT_RENEWAL',       label: 'Renewal Due',           icon: '🔄', color: '#f59e0b', critical: false },
    { key: 'PATIENT_WELCOME',       label: 'Welcome',               icon: '👋', color: '#10b981', critical: false },
  ];

  const GROUP_CONFIG = viewMode === 'patients' || (viewMode === 'mine' && isPatientRole) ? PATIENT_GROUPS : STAFF_GROUPS;

  // Build groups: known types in config order, then 'Other' for anything else
  const grouped = [];
  const knownKeys = new Set(GROUP_CONFIG.map(g => g.key));
  GROUP_CONFIG.forEach(g => {
    const items = active.filter(n => {
      if (n.type === g.key) return true;
      if (n.subType === g.key.toLowerCase()) return true;
      // Fallback: match SYSTEM_CRITICAL by title pattern
      if (g.key === 'SYSTEM_CRITICAL' && (n.title || '').toUpperCase().includes('CRITICAL')) return true;
      return false;
    });
    if (items.length > 0) {
      items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      grouped.push({ ...g, items });
    }
  });
  const groupedIds = new Set(grouped.flatMap(g => g.items.map(n => n._id)));
  const otherItems = active.filter(n => !groupedIds.has(n._id));
  if (otherItems.length > 0) {
    otherItems.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    grouped.push({ key: 'OTHER', label: 'Other', icon: '🔔', color: '#94a3b8', critical: false, items: otherItems });
  }

  // Determine which group should be expanded by default:
  // - If focusId: expand the group containing that notification
  // - Otherwise: expand the group with the most recent notification
  const focusedGroupKey = React.useMemo(() => {
    if (focusId) {
      const fg = grouped.find(g => g.items.some(n => n._id === focusId));
      if (fg) return fg.key;
    }
    // Find group with the latest notification
    let latestTime = 0, latestKey = null;
    for (const g of grouped) {
      if (g.items.length && new Date(g.items[0].createdAt).getTime() > latestTime) {
        latestTime = new Date(g.items[0].createdAt).getTime();
        latestKey = g.key;
      }
    }
    return latestKey;
  }, [focusId, grouped]);

  // Collapsed state: true = collapsed, false = expanded
  const [collapsedGroups, setCollapsedGroups] = React.useState({});
  const toggleGroup = (key) => setCollapsedGroups(prev => ({ ...prev, [key]: prev[key] === true ? false : true }));

  // Expanded details state for grouped notifications — auto-expand focused notification
  const [expandedDetails, setExpandedDetails] = React.useState(() => focusId ? { [focusId]: true } : {});
  const toggleDetails = (id) => setExpandedDetails(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
      <style>{`
        @keyframes notifSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .notif-card {
          background: #1e293b;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          margin-bottom: 12px;
          overflow: hidden;
          animation: notifSlideIn 0.25s ease;
          transition: border-color 0.2s;
        }
        .notif-card.focused {
          border-color: rgba(251,191,36,0.5);
          box-shadow: 0 0 0 2px rgba(251,191,36,0.15);
        }
        .notif-card.unread {
          border-color: rgba(249,115,22,0.30);
        }
        .ack-btn {
          background: rgba(34,197,94,0.12);
          border: 1px solid rgba(34,197,94,0.30);
          color: #4ade80;
          border-radius: 8px;
          padding: 6px 14px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.15s;
        }
        .ack-btn:hover { background: rgba(34,197,94,0.22); }
        .ack-btn:disabled { opacity: 0.45; cursor: default; }
        .tag {
          display: inline-block;
          background: rgba(249,115,22,0.12);
          border: 1px solid rgba(249,115,22,0.25);
          color: #fb923c;
          border-radius: 6px;
          padding: 2px 8px;
          font-size: 11px;
          font-weight: 600;
          margin: 2px 3px 2px 0;
        }
        .task-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background: rgba(139,92,246,0.12);
          border: 1px solid rgba(139,92,246,0.25);
          color: #c4b5fd;
          border-radius: 6px;
          padding: 3px 10px;
          font-size: 11px;
          font-weight: 600;
          margin-top: 8px;
        }
        .notif-group-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          margin: 16px 0 8px;
          border-radius: 10px;
          cursor: pointer;
          user-select: none;
          transition: background 0.15s;
        }
        .notif-group-header:hover { background: rgba(255,255,255,0.04); }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 8, color: '#94a3b8', cursor: 'pointer', padding: '6px 12px', fontSize: 13,
          }}
        >
          ← Back
        </button>
        <h2 style={{ margin: 0, color: '#f1f5f9', fontSize: 20, fontWeight: 800 }}>
          Notifications
        </h2>
        {active.length > 0 && (
          <span style={{
            background: '#ef4444', color: '#fff', borderRadius: 20,
            padding: '2px 9px', fontSize: 12, fontWeight: 800,
          }}>
            {active.length}
          </span>
        )}
        {canViewAll && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 0 }}>
            {[
              { key: 'mine', label: 'My' },
              { key: 'all', label: 'All Staff' },
              { key: 'patients', label: 'Saadhaks' },
            ].map((m, i, arr) => (
              <button key={m.key} onClick={() => setViewMode(m.key)}
                style={{
                  padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: i === 0 ? '6px 0 0 6px' : i === arr.length - 1 ? '0 6px 6px 0' : '0',
                  borderLeft: i > 0 ? 'none' : undefined,
                  background: viewMode === m.key ? '#3b82f6' : 'rgba(255,255,255,0.06)',
                  color: viewMode === m.key ? '#fff' : '#94a3b8',
                }}>
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Empty state ── */}
      {active.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          color: '#475569', fontSize: 15,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔔</div>
          All caught up — no pending notifications.
        </div>
      )}

      {/* ── Grouped notification cards ── */}
      {grouped.map((group, gi) => {
        // Critical groups always expanded. focusedGroupKey is expanded. Others collapsed unless manually toggled.
        const defaultExpanded = group.critical || group.key === focusedGroupKey;
        const isCollapsed = collapsedGroups[group.key] === true || (!defaultExpanded && collapsedGroups[group.key] !== false);
        return (
          <div key={group.key}>
            <div
              className="notif-group-header"
              onClick={() => toggleGroup(group.key)}
              style={{ background: `${group.color}11`, border: `1px solid ${group.color}33` }}
            >
              <span style={{ fontSize: 18 }}>{group.icon}</span>
              <span style={{ flex: 1, color: group.color, fontWeight: 700, fontSize: 14 }}>
                {group.label}
              </span>
              <span style={{
                background: group.color, color: '#fff', borderRadius: 12,
                padding: '1px 8px', fontSize: 11, fontWeight: 700,
              }}>
                {group.items.length}
              </span>
              <span style={{ color: '#64748b', fontSize: 12, marginLeft: 4 }}>
                {isCollapsed ? '▸' : '▾'}
              </span>
            </div>
            {!isCollapsed && group.items.map(n => {
        const isFocused = n._id === focusId;
        return (
          <div
            key={n._id}
            ref={isFocused ? focusRef : null}
            className={`notif-card${isFocused ? ' focused' : ''}${!n.read ? ' unread' : ''}`}
            onClick={() => !n.read && markRead?.(n._id)}
          >
            {/* Card header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 16px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              background: !n.read ? 'rgba(249,115,22,0.05)' : 'transparent',
            }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>
                {(GROUP_CONFIG.find(g => g.key === n.type) || {}).icon
                  || (n.subType === 'plan_approved' ? '✅' : n.subType === 'plan_rejected' ? '❌' : '🔔')}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: '#f1f5f9', fontWeight: 700, fontSize: 14,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {n.title}
                </div>
                <div style={{ color: '#64748b', fontSize: 11, marginTop: 1 }}>
                  {timeAgo(n.createdAt)}
                  {(viewMode === 'all' || viewMode === 'patients') && n.userId && <span style={{ color: '#60a5fa', fontWeight: 600 }}> · @{n.userId}</span>}
                  {n.patientCard && ` · ${n.patientCard}`}
                  {n.patientName && ` · ${n.patientName}`}
                </div>
              </div>
              {!n.read && (
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f97316', flexShrink: 0 }} />
              )}
            </div>

            {/* Card body */}
            <div style={{ padding: '12px 16px' }}>
              <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>
                {n.message}
              </div>

              {/* Expandable grouped details */}
              {n.details && n.details.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <button
                    onClick={e => { e.stopPropagation(); toggleDetails(n._id); }}
                    style={{
                      background: 'rgba(99,102,241,0.10)', border: '1px solid rgba(99,102,241,0.25)',
                      color: '#a5b4fc', borderRadius: 8, padding: '5px 12px', fontSize: 12,
                      fontWeight: 700, cursor: 'pointer', marginBottom: 8,
                    }}
                  >
                    {expandedDetails[n._id] ? '▾ Hide' : '▸ Show'} {n.itemCount || n.details.length} item(s)
                  </button>
                  {expandedDetails[n._id] && (
                    <div style={{
                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 10, padding: '8px 0', maxHeight: 300, overflowY: 'auto',
                    }}>
                      {n.details.map((d, di) => (
                        <div key={di} style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px',
                          borderBottom: di < n.details.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                          fontSize: 12,
                        }}>
                          <span style={{ color: '#64748b', fontWeight: 700, minWidth: 20 }}>{di + 1}.</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ color: '#e2e8f0', fontWeight: 600 }}>
                              {d.patientName || d.title || d.patientCard || '—'}
                            </span>
                            {d.patientCard && <span style={{ color: '#64748b', marginLeft: 6 }}>({d.patientCard})</span>}
                            {d.phone && <span style={{ color: '#60a5fa', marginLeft: 6 }}>{d.phone}</span>}
                            {d.therapist && <span style={{ color: '#94a3b8', marginLeft: 6 }}>· {d.therapist}</span>}
                          </div>
                          <div style={{ color: '#94a3b8', fontSize: 11, whiteSpace: 'nowrap' }}>
                            {d.daysSince != null && `${d.daysSince}d ago`}
                            {d.daysOverdue != null && `${d.daysOverdue}d overdue`}
                            {d.daysDelayed != null && `${d.daysDelayed}d delayed`}
                            {d.pendingVisits != null && `${d.pendingVisits} left`}
                            {d.paymentStatus && d.paymentStatus}
                            {d.visitCharges ? ` · ₹${d.visitCharges}` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Pending fields */}
              {(n.pendingFields || []).length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  {n.pendingFields.map(f => (
                    <span key={f} className="tag">
                      {FIELD_LABELS[f] || f}
                    </span>
                  ))}
                </div>
              )}

              {/* Auto-task badge */}
              {n.autoTaskCreated && (
                <div className="task-badge">
                  <span>✅</span>
                  High-priority task created
                </div>
              )}

              {/* Acknowledge button OR Dismiss button */}
              {(() => {
                const isDismissType = ['KYC_UPDATE', 'PASSWORD_CHANGED', 'CHECKLIST_MISSED', 'TASK_REMINDER', 'MORNING_GREETING'].includes(n.type) || n.isPatient;
                if (!isDismissType) return (
              <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
                <button
                  className="ack-btn"
                  disabled={n.acknowledged}
                  onClick={async e => {
                    e.stopPropagation();
                    await acknowledgeNotification?.(n._id);
                  }}
                >
                  {n.acknowledged ? '✓ Acknowledged' : 'Acknowledge'}
                </button>
                {n.acknowledged && !n.autoTaskCreated && (
                  <span style={{ color: '#64748b', fontSize: 11 }}>
                    Acknowledged {timeAgo(n.acknowledgedAt)}
                  </span>
                )}
                {n.acknowledged && n.autoTaskCreated && (
                  <span style={{ color: '#a78bfa', fontSize: 11 }}>
                    Task auto-created — check OTT Dashboard
                  </span>
                )}
              </div>
                );
                return (
              <div style={{ marginTop: 8 }}>
                <button
                  className="ack-btn"
                  onClick={async e => {
                    e.stopPropagation();
                    await acknowledgeNotification?.(n._id);
                  }}
                  style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.30)', color: '#a5b4fc' }}
                >
                  Dismiss
                </button>
              </div>
                );
              })()}
            </div>
          </div>
        );
      })}
          </div>
        );
      })}
    </div>
  );
}
